import { useEffect, useRef } from "react";
import { takeSignInChoice } from "./theme-cookie.js";
import type { PresentationTheme } from "./types.js";
import { usePresentationPreferencesQuery } from "./useSettings.js";

/**
 * Saves a theme picked on the sign-in page as the person's setting, once,
 * right after sign-in (ADR-0092, 2026-10-06 amendment, d5).
 *
 * - ONE OWNER: called only in `AppLayout`. `usePresentationTheme` is mounted
 *   twice (AppLayout and Settings > Account), so the step cannot live there.
 * - ORDER MATTERS: `AppLayout` calls this AFTER `usePresentationTheme()` and
 *   passes that hook's `setTheme`. React runs effects in hook order, so the load
 *   applies the saved setting first and this step then applies the choice, in
 *   the same effect pass, before the browser paints.
 * - The choice comes from the sign-in page's mark only, never from `pb_theme`
 *   or the store: loading the saved setting has already rewritten both.
 * - It waits for a successful read. A failed read leaves the mark for a reload
 *   within its 30 minutes. Once the setting is loaded, the mark is read and
 *   deleted in one step, and the ref keeps StrictMode's second run a no-op, so
 *   at most one save is sent.
 * - A newer change wins: when the saved setting's `updatedAt` is later than the
 *   click, nothing is saved. A setting that was never saved has
 *   `updatedAt: null`, which never counts as newer. Saving the value already
 *   saved sends nothing.
 */
export function useSignInThemeChoice(
  setTheme: (next: PresentationTheme) => void,
): void {
  const saved = usePresentationPreferencesQuery().data;
  const ran = useRef(false);

  useEffect(() => {
    if (!saved || ran.current) return;
    ran.current = true;
    const choice = takeSignInChoice();
    if (!choice) return;
    const savedAt =
      saved.updatedAt === null
        ? Number.NEGATIVE_INFINITY
        : Date.parse(saved.updatedAt);
    if (savedAt > choice.chosenAt || choice.theme === saved.theme) return;
    setTheme(choice.theme);
  }, [saved, setTheme]);
}
